import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Add, Delete, Description, Edit, MoreVert } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { observer } from 'mobx-react';
import { formatCurrency } from '@roc/ui/utils';
import { format as formatDate } from 'date-fns';
import { useStore } from '@roc/client-portal-shared/hooks';
import { BankStatementOtherSource } from '../stores/bankStatementsStore';

export interface OtherSourcesTableProps {
  classes: Record<string, string>;
  showActions: boolean;
  onAdd: () => void;
  onEdit: (record: BankStatementOtherSource) => void;
  onRemove: (record: BankStatementOtherSource) => void;
}

const formatCreatedDate = (value: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return formatDate(date, 'MMM d, yyyy');
};

export const OtherSourcesTable = observer(
  ({
    classes,
    showActions,
    onAdd,
    onEdit,
    onRemove,
  }: OtherSourcesTableProps) => {
    const { bankStatementsStore } = useStore();
    const { otherSources } = bankStatementsStore;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [
      activeRecord,
      setActiveRecord,
    ] = useState<BankStatementOtherSource | null>(null);

    const handleMenuOpen = (
      e: React.MouseEvent<HTMLElement>,
      record: BankStatementOtherSource
    ) => {
      setAnchorEl(e.currentTarget);
      setActiveRecord(record);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setActiveRecord(null);
    };

    const handleEdit = () => {
      const record = activeRecord;
      handleMenuClose();
      if (record) onEdit(record);
    };

    const handleRemove = () => {
      const record = activeRecord;
      handleMenuClose();
      if (record) onRemove(record);
    };

    return (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <Typography className={classes.tableTitle} component="div">
            Other Sources
          </Typography>
          {showActions && (
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<Add />}
              onClick={onAdd}
            >
              Add
            </Button>
          )}
        </div>

        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Amount</TableCell>
                <TableCell className={classes.headerCell}>Comments</TableCell>
                <TableCell className={classes.headerCell} align="center">
                  Added By
                </TableCell>
                {showActions && (
                  <TableCell className={classes.headerCell} align="center">
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {(otherSources ?? []).map(record => (
                <TableRow key={record.bankStatementOtherSourceId}>
                  <TableCell className={classes.bodyCell}>
                    {formatCurrency(record.amount)}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {record.comments || '—'}
                  </TableCell>
                  <TableCell className={classes.bodyCell} align="center">
                    {record.createdBy ? (
                      <Tooltip
                        title={
                          record.createdDate ? (
                            <span className={classes.tooltipContent}>
                              {`Added: ${record.createdDate}`}
                            </span>
                          ) : (
                            ''
                          )
                        }
                        placement="top"
                      >
                        <span>
                          <Typography variant="body2">
                            {record.createdBy}
                          </Typography>
                          {record.createdDate && (
                            <Typography variant="caption" color="textSecondary">
                              {formatCreatedDate(record.createdDate)}
                            </Typography>
                          )}
                        </span>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  {showActions && (
                    <TableCell className={classes.bodyCell} align="center">
                      <IconButton
                        size="small"
                        onClick={e => handleMenuOpen(e, record)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(otherSources ?? []).length === 0 && (
            <div className={classes.emptyState}>
              <div className={classes.emptyStateIcon}>
                <Description style={{ color: '#94A3B8', fontSize: 24 }} />
              </div>
              <Typography className={classes.emptyStateTitle}>
                No other sources added yet
              </Typography>
              {showActions && (
                <Typography variant="body2" color="textSecondary">
                  Use Add to record liquidity that has no bank statement
                </Typography>
              )}
            </div>
          )}
        </TableContainer>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          getContentAnchorEl={null}
        >
          <MenuItem onClick={handleEdit}>
            <Edit className={classes.menuIcon} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleRemove} className={classes.deleteItem}>
            <Delete className={classes.menuIcon} />
            Remove
          </MenuItem>
        </Menu>
      </>
    );
  }
);
